import React, { FC } from 'react';
import { InnowiseLogo } from '../../../../assets/svg';
import styled, { css, keyframes } from 'styled-components';
import { COLORS, device } from '../../../../constants';
import { techIconMap } from '../../../../assets/techIcons';
import useAnimation from '../../../../hooks/useAnimation';

interface TechnologyElementProps {
  src: string;
  index: number;
  total: number;
  rotationOffset: number;
  isAnimating: boolean;
  isMoving: boolean;
}

const TechnologyAnimate: FC = () => {
  const techIcons: string[] = [
    techIconMap.ReactNative,
    techIconMap.Maui,
    techIconMap.Mendix,
    techIconMap.Flutter,
    techIconMap.NodejsPlatform,
    techIconMap.React,
    techIconMap['Android SDK'],
    techIconMap.SupabaseLogo,
    techIconMap.Tizen,
    techIconMap.Ios,
  ];
  const {
    rotationOffset,
    isAnimated,
    isMoving,
    handleMouseEnter,
    handleMouseLeave,
  } = useAnimation();

  return (
    <>
      <AnimatedBlock
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        $isAnimating={isAnimated}
      >
        <TechnologiesCircle>
          {techIcons.map((src, index) => (
            <TechnologyElement
              key={index}
              src={src}
              index={index}
              total={techIcons.length}
              rotationOffset={rotationOffset}
              isAnimating={isAnimated}
              isMoving={isMoving}
            />
          ))}
        </TechnologiesCircle>
        <SvgConnections $isMoving={isMoving} $isAnimating={isAnimated} />
      </AnimatedBlock>
      <LogoWrapper>
        <InnowiseLogo color={COLORS.WHITE} />
      </LogoWrapper>
    </>
  );
};

const colorAnimation = keyframes`
    0% , 100%{
        border-color: ${COLORS.ACTIVE_ITEM};
    }
    33%, 66% {
        border-color:${COLORS.RED_BORDER};
    }
`;

const AnimatedBlock = styled.div<{ $isAnimating: boolean }>`
  width: 592px;
  height: 592px;
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -moz-user-select: none;
`;

const TechnologyElement: FC<TechnologyElementProps> = ({
  src,
  index,
  total,
  rotationOffset,
  isAnimating,
  isMoving,
}) => {
  const angle = ((index + rotationOffset) / total) * 360;
  return (
    <TechnologyWrapper
      $isAnimating={isAnimating}
      $isMoving={isMoving}
      $angle={angle}
    >
      <TechIMG src={src} />
    </TechnologyWrapper>
  );
};

const TechnologyWrapper = styled.div<{
  $angle: number;
  $isAnimating: boolean;
  $isMoving: boolean;
}>`
  width: 80px;
  height: 80px;
  background: ${COLORS.WHITE};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.ACTIVE_ITEM};
  border-radius: 50%;
  position: absolute;
  left: 50%;
  z-index: 5;
  top: 50%;
  will-change: transform;
  transform-origin: center;
  backface-visibility: hidden;
  transition: transform 2s ease-in-out;
  transform: ${({ $angle }) => `
    translate3d(-50%, -50%, 0)
    rotate(${$angle}deg)
    translate3d(calc((var(--trans) - 166px) / 2 + 40px), 0, 0)
    rotate(-${$angle}deg)
  `};
  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      animation: ${colorAnimation} 6s forwards;
    `}
  ${({ $isMoving }) => ($isMoving ? ` --trans: 728px;` : `--trans: 592px;`)}
  @media screen and ${device.lg} {
    width: 50px;
    height: 50px;
    transform: ${({ $angle }) => `
    translate(-50%, -50%) rotate(${$angle}deg) translate(calc((var(--trans) - 111px) / 2 + 25px)) rotate(-${$angle}deg)`};
    ${({ $isMoving }) => ($isMoving ? ` --trans: 485px;` : `--trans: 395px;`)}
  }
  @media screen and ${device.md} {
    width: 25px;
    height: 25px;
    transform: ${({ $angle }) => `
    translate(-50%, -50%) rotate(${$angle}deg) translate(calc((var(--trans) - 55px) / 2 + 12px)) rotate(-${$angle}deg)`};
    ${({ $isMoving }) => ($isMoving ? ` --trans: 243px;` : `--trans: 197px;`)}
  }
`;

const LogoWrapper = styled.div`
  background: ${COLORS.WHITE};
  width: 332px;
  height: 332px;
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-sizing: border-box;
  svg {
    width: 300px;
    height: 180px;
  }
  @media screen and ${device.lg} {
    width: 220px;
    height: 220px;
    svg {
      width: 150px;
    }
  }
  @media screen and ${device.md} {
    width: 110px;
    height: 110px;
    svg {
      width: 80px;
    }
  }
`;

const TechIMG = styled.img`
  width: 40px;
  height: 40px;
  @media screen and ${device.lg} {
    width: 26px;
    height: 26px;
  }
  @media screen and ${device.md} {
    width: 13px;
    height: 13px;
  }
`;

const TechnologiesCircle = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const SvgConnections = styled.div<{
  $isAnimating: boolean;
  $isMoving: boolean;
}>`
  ${({ $isMoving }) => ($isMoving ? ` --trans: 648px;` : `--trans: 512px;`)}
  width: var(--trans);
  height: var(--trans);
  box-sizing: border-box;
  transition:
    width 2s ease-in-out,
    height 2s ease-in-out;
  will-change: width, height;
  position: absolute;
  border: 1px dashed ${COLORS.ACTIVE_ITEM};
  border-radius: 50%;
  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      animation: ${colorAnimation} 6s infinite ease-in-out;
    `}
  @media screen and ${device.lg} {
    ${({ $isMoving }) => ($isMoving ? ` --trans: 435px;` : `--trans: 345px;`)}
  }
  @media screen and ${device.md} {
    ${({ $isMoving }) => ($isMoving ? ` --trans: 218px;` : `--trans: 172px;`)}
  }
`;

export default TechnologyAnimate;
